/**
 * KuCoin timeframe configuration and interval mapping.
 * @see https://www.kucoin.com/docs/rest/spot-trading/market-data/get-klines
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * KuCoin supports 1-minute minimum for klines, but has ticker for real-time.
 */
export const KUCOIN_TIMEFRAMES: TimeframeConfig = {
  native: [
    '1min',
    '3min',
    '5min',
    '15min',
    '30min',
    '1hour',
    '2hour',
    '4hour',
    '6hour',
    '8hour',
    '12hour',
    '1day',
    '1week',
    '1month',
  ],
  synthetic: ['1s'], // Use ticker for 1s updates
  minIntervalSeconds: 60, // Klines start at 1 minute
};

// Register with central registry
registerExchangeTimeframes('kucoin', KUCOIN_TIMEFRAMES);

/**
 * Maps TradingView resolution to KuCoin interval format.
 */
export const mapToKucoinInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds < 60) return '1min'; // Use 1min for sub-minute (will use ticker)
  if (seconds === 60) return '1min';
  if (seconds === 180) return '3min';
  if (seconds === 300) return '5min';
  if (seconds === 900) return '15min';
  if (seconds === 1800) return '30min';
  if (seconds === 3600) return '1hour';
  if (seconds === 7200) return '2hour';
  if (seconds === 14400) return '4hour';
  if (seconds === 21600) return '6hour';
  if (seconds === 28800) return '8hour';
  if (seconds === 43200) return '12hour';
  if (seconds === 86400) return '1day';
  if (seconds === 604800) return '1week';
  if (seconds === 2592000) return '1month'; // 30 days

  // Default to closest supported interval
  if (seconds < 180) return '1min';
  if (seconds < 300) return '3min';
  if (seconds < 604800) return '1hour';
  return '1day';
};
